import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface BarSegment {
  value: number;
  color: string;
  label: string;
}

interface StackedBarItem {
  label: string;
  segments: BarSegment[];
  total?: number;
}

interface EnhancedStackedBarChartProps {
  data: StackedBarItem[];
  alternativeData?: StackedBarItem[];
  title?: string;
  height?: number;
  maxValue?: number;
  showValue?: boolean;
  className?: string;
  valueSuffix?: string;
  showLegend?: boolean;
  showYAxis?: boolean;
  yAxisStep?: number;
}

const EnhancedStackedBarChart: React.FC<EnhancedStackedBarChartProps> = ({
  data,
  alternativeData,
  title,
  height = 300,
  maxValue,
  showValue = true,
  className = '',
  valueSuffix = '',
  showLegend = true,
  showYAxis = true,
  yAxisStep = 20,
}) => {
  const [filterType, setFilterType] = useState<'level' | 'language'>('level');

  // Get the correct data based on selected filter
  const currentData =
    filterType === 'level' || !alternativeData ? data : alternativeData;

  // Format data for Recharts
  const transformedData = currentData.map((item) => {
    const result: any = { name: item.label };

    item.segments.forEach((segment) => {
      if (segment.label) {
        result[segment.label] = segment.value;
      }
    });

    // Add total for tooltip
    result._total =
      item.total ||
      item.segments.reduce((acc, segment) => acc + segment.value, 0);

    return result;
  });

  // Extract unique segment labels for the bars
  const uniqueSegmentLabels = Array.from(
    new Set(
      currentData
        .flatMap((item) => item.segments)
        .filter((segment) => segment.label)
        .map((segment) => segment.label),
    ),
  ) as string[];

  // Get colors for each segment
  const colorMap: Record<string, string> = {};
  currentData.forEach((item) => {
    item.segments.forEach((segment) => {
      if (segment.label) {
        colorMap[segment.label] = segment.color;
      }
    });
  });

  // Custom tooltip to show total
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload[0].payload._total;

      return (
        <div className="custom-tooltip bg-white p-2 border border-gray-200 shadow-md rounded-md">
          <p className="label font-medium text-sm">{`${label}`}</p>
          <p className="total text-base font-bold mb-1">{`Total: ${total}`}</p>
          <hr className="my-1" />
          {payload.map((entry: any, index: number) => (
            <p
              key={`item-${index}`}
              className="text-xs"
              style={{ color: entry.color }}
            >
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`w-full ${className}`}>
      {title && <h3 className="text-lg font-medium mb-2">{title}</h3>}

      {/* Filter tabs */}
      <div className="flex justify-end mb-2">
        <div className="inline-flex rounded-lg shadow-sm bg-gray-100 p-0.5">
          <button
            onClick={() => setFilterType('level')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              filterType === 'level'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            By Level
          </button>
          <button
            onClick={() => setFilterType('language')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              filterType === 'language'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-700 hover:bg-gray-200'
            }`}
            disabled={!alternativeData}
          >
            By Language
          </button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={transformedData}
          margin={{
            top: 5,
            right: 10,
            left: 10,
            bottom: 5,
          }}
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tickMargin={5} />
          {showYAxis && (
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) =>
                Number.isInteger(value) ? value.toString() : ''
              }
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend wrapperStyle={{ paddingTop: 0 }} />}

          {uniqueSegmentLabels.map((segmentLabel) => (
            <Bar
              key={segmentLabel}
              dataKey={segmentLabel}
              stackId="a"
              fill={colorMap[segmentLabel] || '#8884d8'}
              name={segmentLabel}
              label={
                showValue && filterType === 'level'
                  ? { position: 'center', fill: 'white', fontSize: 11 }
                  : false
              }
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EnhancedStackedBarChart;
